import React from 'react';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import TableCell from 'components/table/TableCell';
import Table from 'components/table/Table';
import {SearchType} from 'types/search';
import BlueButton from 'components/BlueButton';
import PlusIcon from 'assets/images/icons/PlusIcon';
import DotsMenu from 'components/DotsMenu';
import {useDropdownActionMenu} from 'hooks/useDropdownActionMenu';
import TrashIcon from 'assets/images/icons/TrashIcon';
import usePermissions from 'hooks/usePermissions';
import {DocumentTemplateElement} from 'types/documentTemplateElement';
import {
  setIsEditing,
  setDocumentTemplateElement,
} from './documentTemplateElementSlice';
import {DocumentTemplateElementThunks} from './documentTemplateElementThunks';
import {setActiveTab, PROGRAM_TABS} from 'features/programs/programsSlice';
import CheckCircleIcon from 'assets/images/icons/CheckCircleIcon';
import XCircleIcon from 'assets/images/icons/XCircleIcon';
import {DocumentTemplateElementOptions} from 'constants/documentTemplateElementOptions';
import {useParams} from 'react-router-dom';

const DocumentTemplateElementTable: React.FC<{openDrawer: () => void}> = ({
  openDrawer,
}) => {
  const documentTemplateElements = useSelector(
    (state: RootState) =>
      state.documentTemplateElement.documentTemplateElements,
  );
  const dispatch = useDispatch<AppDispatch>();
  const {setDotsMenuIsOpen} = useDropdownActionMenu();
  const {checkPermission} = usePermissions();
  const {programId} = useParams<{programId: string}>();
  const pagy = useSelector(
    (state: RootState) => state.documentTemplateElement.pagy,
  );
  const loading = useSelector(
    (state: RootState) => state.program.status === 'loading',
  );

  interface VisibleColumnsType {
    name: boolean;
    type: boolean;
    scope: boolean;
    defaultValue: boolean;
    demographic: boolean;
  }

  const [visibleColumns, setVisibleColumns] =
    React.useState<VisibleColumnsType>({
      name: true,
      type: true,
      scope: true,
      defaultValue: true,
      demographic: true,
    });

  const columnOptions = [
    {label: 'Name', value: 'name', isVisible: visibleColumns.name},
    {label: 'Type', value: 'type', isVisible: visibleColumns.type},
    {label: 'Scope', value: 'scope', isVisible: visibleColumns.scope},
    {
      label: 'Default Value',
      value: 'defaultValue',
      isVisible: visibleColumns.defaultValue,
    },
    {
      label: 'Demographic',
      value: 'demographic',
      isVisible: visibleColumns.demographic,
    },
  ];

  const searchByOptions = [{label: 'Name', value: 'name', operator: 'like'}];

  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    search.where.elementable_type = {
      operator: '==',
      value: 'Program',
    };
    search.where.elementable_id = {
      operator: 'in',
      valueArray: [null, programId],
    };
    await dispatch(
      DocumentTemplateElementThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );
  };

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleOpenNewCustomData = () => {
    dispatch(setDocumentTemplateElement(undefined));
    dispatch(setIsEditing(true));
    openDrawer();
  };

  const handleDelete = (id: string) => {
    setDotsMenuIsOpen(null);
    dispatch(DocumentTemplateElementThunks.delete(id));
  };

  const handleEdit = (id: string) => {
    dispatch(setIsEditing(true));
    dispatch(DocumentTemplateElementThunks.show(id)).then(() => {
      openDrawer();
    });
  };

  return (
    <Table
      fetchData={fetchData}
      pagy={pagy}
      pageable={true}
      tabs={PROGRAM_TABS}
      activeTab={'Custom Data'}
      setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
      onColumnToggle={handleColumnToggle}
      searchByOptions={searchByOptions}
      recordsName={'Fields'}
      columnOptions={columnOptions}
      searchable={true}
      loading={loading}
      columns={true}
      blueButton={
        checkPermission('create', 'DocumentTemplateElement') ? (
          <BlueButton
            onClick={handleOpenNewCustomData}
            label=""
            icon={<PlusIcon />}
          />
        ) : null
      }>
      <thead>
        <tr>
          {visibleColumns.name && <TableHeader label="Name" sortBy="name" />}
          {visibleColumns.type && <TableHeader label="Type" />}
          {visibleColumns.scope && <TableHeader label="Scope" />}
          {visibleColumns.defaultValue && (
            <TableHeader label="Default Value" sortBy="defaultValue" />
          )}
          {visibleColumns.demographic && (
            <TableHeader label="Demographic" sortBy="demographic" />
          )}
          <TableHeader label="" />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
        {documentTemplateElements?.map(
          (documentTemplateElement: DocumentTemplateElement) => {
            const userOptions = [];
            if (checkPermission('update', 'DocumentTemplateElement')) {
              userOptions.push({
                label: 'Edit',
                onClick: () => handleEdit(documentTemplateElement.id),
                hoverClass: 'hover:bg-cyan-100',
              });
            }
            if (checkPermission('delete', 'DocumentTemplateElement')) {
              userOptions.push({
                label: 'Delete',
                onClick: () => handleDelete(documentTemplateElement.id),
                icon: <TrashIcon />,
                hoverClass: 'hover:bg-red-100 hover:text-red-800',
              });
            }
            return (
              <TableRow key={documentTemplateElement.id}>
                {visibleColumns.name && (
                  <TableCell>
                    <div className="relative group">
                      <div className="w-full flex items-center gap-x-3">
                        <div className="flex-grow">
                          <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                            {documentTemplateElement.properties?.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                )}
                {visibleColumns.type && (
                  <TableCell>
                    {DocumentTemplateElementOptions.find(
                      option =>
                        option.value === documentTemplateElement.elementType,
                    )?.label || 'Unknown Type'}
                  </TableCell>
                )}
                {visibleColumns.scope && (
                  <TableCell>
                    {documentTemplateElement.elementableId
                      ? 'This program'
                      : 'All programs for Client'}
                  </TableCell>
                )}
                {visibleColumns.defaultValue && (
                  <TableCell>
                    {documentTemplateElement.properties?.defaultValue}
                  </TableCell>
                )}
                {visibleColumns.demographic && (
                  <TableCell>
                    {documentTemplateElement.properties?.visibility ===
                    'demographics' ? (
                      <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <XCircleIcon className="w-5 h-5 text-red-500" />
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <DotsMenu options={userOptions} />
                </TableCell>
              </TableRow>
            );
          },
        )}
      </tbody>
    </Table>
  );
};

export default DocumentTemplateElementTable;
